<script setup lang="ts">
import {computed, ref, watch} from "vue";
import debounce from "lodash.debounce";
import {searchBarApi} from "@/container";
import {
    AUTO_COMPLETE_REQUEST_TYPE,
    SearchBarAutoCompleteApiResponse,
    SearchBarAutoCompleteItem
} from "@/auto-complete-searchbar/interfaces/ISearchBarAutoComplete";
import {AUTO_COMPLETE_SECTION} from "@/auto-complete-searchbar/interfaces";
import sectionsConfig from "@/core/factories/search-bar/AutoCompleteConfig";
import {onClickOutside, useElementBounding, useWindowSize} from "@vueuse/core";
import {CircleX} from "@lucide/vue";
import {Input} from "@/core/components/ui/input";
import {Button} from "@/core/components/ui/button";
import AutoCompleteSearchbarTag from "@/auto-complete-searchbar/components/searchbar/AutoCompleteSearchbarTag.vue";
import ScrollableArea from "@/auto-complete-searchbar/components/searchbar/ScrollableArea.vue";
import {motion, AnimatePresence} from "motion-v";
import {Checkbox} from "@/core/components/ui/checkbox";

const {
    tags = true,
    requestTypes = [
        AUTO_COMPLETE_REQUEST_TYPE.PRODUCT,
        AUTO_COMPLETE_REQUEST_TYPE.LABORATORY,
        AUTO_COMPLETE_REQUEST_TYPE.RANGE
    ],
    placeholder = "Rechercher un produit, cip, laboratoire, etc...",
    size = 5
} = defineProps<{ tags?: boolean; requestTypes?: AUTO_COMPLETE_REQUEST_TYPE[]; placeholder?: string; size?: number }>();
const selectedItems = defineModel<SearchBarAutoCompleteItem[]>('selectedItems', {
    default: () => []
});

const search = ref("");
const open = ref(false);
const loading = ref(false);
const results = ref<SearchBarAutoCompleteApiResponse | null>(null);
const containerRef = ref<HTMLElement | null>(null);

const MIN_SEARCH_LENGTH = 3;
const DEBOUNCE_DELAY = 400;
const PANEL_MAX_HEIGHT = 448;
const PANEL_MIN_HEIGHT = 240;
const PANEL_BOTTOM_MARGIN = 16;
const TAG_ROW_HEIGHT = 48;

const {
    top: containerTop,
    height: containerHeight,
    update: updateContainerBounding
} = useElementBounding(containerRef, {windowScroll: false});
const {height: windowHeight} = useWindowSize();

/**
 * The results panel height: fixed, but clamped to the space available between
 * the panel and the bottom of the viewport.
 */
const panelHeight = computed(() => {
    const reserved = tags && !selectedItems.value.length ? TAG_ROW_HEIGHT : 0;
    const available = windowHeight.value - containerTop.value - containerHeight.value - reserved - PANEL_BOTTOM_MARGIN;

    return `${Math.round(Math.min(PANEL_MAX_HEIGHT, Math.max(PANEL_MIN_HEIGHT, available)))}px`;
});

const fetchResults = debounce(async (value: string) => {
    loading.value = true;
    try {
        results.value = await searchBarApi().autocomplete(value, requestTypes, size);
    } catch {
        results.value = null;
    } finally {
        loading.value = false;
    }
}, DEBOUNCE_DELAY);

watch(search, (value) => {
    if (value.length > MIN_SEARCH_LENGTH) {
        fetchResults(value);
    } else {
        results.value = null;
    }
});

onClickOutside(containerRef, () => open.value = false);

const panelVisible = computed(() => open.value && search.value.length > MIN_SEARCH_LENGTH);

// Re-measure the container position each time the panel opens, as the page
// may have been scrolled since the last measurement.
watch(panelVisible, (visible) => {
    if (visible) {
        updateContainerBounding();
    }
});

/**
 * The sections that will be displayed.
 * The displayed sections should match the request types.
 */
const sections = computed(() => {
    if (!results.value) {
        return results.value;
    }

    return Object.keys(results.value)
        .filter(section => {
            const config = sectionsConfig[section as AUTO_COMPLETE_SECTION];
            return config && requestTypes.includes(config.requestType);
        })
        .map(section => {
            const sectionKey = section as AUTO_COMPLETE_SECTION;
            const items = results.value![sectionKey];
            const config = sectionsConfig[sectionKey];

            return {
                type: sectionKey,
                label: config?.name || section,
                color: config?.color || "#E2E8F0",
                position: config?.position || 999,
                items: items?.length ? items : null
            };
        })
        .sort((a, b) => a.position - b.position);
});

/**
 * Toggle item selection.
 */
const toggleSelection = (item: SearchBarAutoCompleteItem) => {
    const index = selectedItems.value.findIndex(
        selectedItem => selectedItem.id === item.id && selectedItem.type === item.type
    );

    if (index !== -1) {
        selectedItems.value = selectedItems.value.filter(
            selectedItem => selectedItem.id !== item.id || selectedItem.type !== item.type
        );
    } else {
        selectedItems.value = [...selectedItems.value, item];
    }
};

/**
 * Clear the search and remove all selected filters.
 */
const clearAll = () => {
    search.value = "";
    selectedItems.value = [];
};

/**
 * Validate the selection: close the results panel, keeping the selected filters.
 */
const validateSelection = () => {
    search.value = "";
    open.value = false;
};

const isItemSelected = (itemId: number | string, type: string) => {
    return selectedItems.value.some(item => item.id === itemId && item.type === type);
};
</script>

<template>
  <div ref="containerRef" class="relative w-full">
    <div class="relative">
      <Input
        v-model:model-value="search"
        class="flex-1"
        :placeholder="placeholder"
        :class="{ 'rounded-b-none': (!tags || !selectedItems.length) && open && search.length > MIN_SEARCH_LENGTH }"
        @focus="open = true"
      />
      <div v-if="search || selectedItems.length" class="absolute right-2 top-2 cursor-pointer" @click="clearAll">
        <CircleX class="w-5 h-5" />
      </div>
    </div>
    <div v-if="tags && selectedItems?.length" class="relative">
      <div
        v-if="selectedItems.length > 0"
        class="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent pointer-events-none z-10"
      />
      <div class="flex gap-2 overflow-x-auto py-2">
        <AnimatePresence mode="popLayout">
          <motion.div
            v-for="item in selectedItems"
            :key="`${item.type}_${item.id}`"
            :initial="{ opacity: 0 }"
            :animate="{ opacity: 1 }"
            :exit="{ opacity: 0, y: 100 }"
            :transition="{ duration: 0.25, type: 'tween' }"
            layout
            class="shrink-0"
          >
            <AutoCompleteSearchbarTag :item="item" @remove="toggleSelection" />
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
    <Transition name="fold">
      <div
        v-if="panelVisible"
        class="absolute z-50 w-full bg-white shadow-lg rounded-b-md overflow-hidden"
      >
        <div v-if="loading" class="h-1">
          <div class="w-full h-1 bg-gray-100 rounded-full overflow-hidden">
            <div class="loading-bar h-full rounded-full" />
          </div>
        </div>

        <div class="p-4" :style="sections?.length ? { height: panelHeight } : undefined">
          <div
            v-if="sections?.length"
            class="grid grid-cols-3 grid-rows-2 gap-4 h-full"
          >
            <div
              v-for="section in sections"
              :key="section.type"
              class="rounded-md p-3 flex flex-col min-h-0 overflow-hidden"
              :style="{ backgroundColor: `${section.color}20` }"
            >
              <h3
                class="text-sm font-semibold mb-2 px-2 py-1 rounded-md self-start shrink-0"
                :style="{ backgroundColor: section.color, color: '#FFF' }"
              >
                {{ section.label }}
              </h3>

              <ScrollableArea v-if="section.items">
                <div class="space-y-2">
                  <div
                    v-for="item in section.items"
                    :key="item.id"
                    class="flex gap-x-2"
                    :class="{
                      'items-center': !item.cip && section.type !== AUTO_COMPLETE_SECTION.VIDAL_CATEGORY,
                      'items-start': item.cip || section.type === AUTO_COMPLETE_SECTION.VIDAL_CATEGORY
                    }"
                  >
                    <Checkbox
                      :id="`${section.type}_${item.id}`"
                      :model-value="isItemSelected(item.id, section.type)"
                      @update:model-value="toggleSelection(item)"
                    />
                    <div class="grid gap-1 leading-none">
                      <label class="text-sm font-medium" :for="`${section.type}_${item.id}`">
                        {{ item.name }}
                      </label>
                      <p v-if="item.cip" class="text-xs text-gray-500">
                        CIP: {{ item.cip }}
                      </p>
                      <p
                        v-if="section.type === AUTO_COMPLETE_SECTION.VIDAL_CATEGORY && item.meta"
                        class="text-xs text-gray-500"
                      >
                        {{ item.code }} · {{ item.meta }}
                      </p>
                    </div>
                  </div>
                </div>
              </ScrollableArea>

              <div v-else class="text-sm text-gray-500 italic p-2">
                Aucun résultat trouvé
              </div>
            </div>
          </div>

          <div v-if="sections?.length === 0" class="text-center py-4 text-sm font-medium text-gray-500">
            Aucun résultat trouvé pour "{{ search }}"
          </div>
        </div>

        <Button
          v-if="sections?.length && selectedItems.length"
          class="absolute bottom-3 right-3 bg-primary text-white hover:bg-primary/90 shadow-lg"
          @click="validateSelection"
        >
          Valider
        </Button>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.fold-enter-active,
.fold-leave-active {
    transition: all 0.3s ease-out;
    transform-origin: top center;
}

.fold-enter-from,
.fold-leave-to {
    transform: scaleY(0);
}

.fold-enter-to,
.fold-leave-from {
    transform: scaleY(1);
}

.loading-bar {
    background: linear-gradient(
            90deg,
            theme('colors.primary.DEFAULT'),
            theme('colors.secondary.DEFAULT'),
            theme('colors.primary.DEFAULT')
    );
    background-size: 200% 100%;
    animation: loading-animation 3s infinite linear;
}

@keyframes loading-animation {
    0% {
        background-position: 100% 50%;
        width: 0;
    }
    50% {
        width: 70%;
    }
    100% {
        background-position: 0 50%;
        width: 100%;
    }
}
</style>
